import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Gauge } from "lucide-react";
import { useI18n } from "../i18n";
import { formatMoney } from "../lib/format";
import { planName } from "../lib/plans";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

/** Plan usage banner: warns at 80%, blocks visibly at 100%. */
export const QuotaBanner = ({ quota, showUpgrade = true, className = "" }) => {
  const { t } = useI18n();
  if (!quota || !quota.included) return null;
  const pct = Math.min(100, quota.pct || 0);
  if (!quota.blocked && pct < 80) return null;
  const blocked = Boolean(quota.blocked);
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${
        blocked ? "border-red-200 bg-red-50" : "border-amber-200 bg-amber-50"
      } ${className}`}
      data-testid="quota-banner"
      data-blocked={blocked ? "true" : "false"}
    >
      <div className="flex min-w-0 items-start gap-3">
        {blocked ? (
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-700" />
        ) : (
          <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">
            {blocked
              ? t("scan.blockedTitle")
              : t("dash.trialWarning", { used: quota.used, total: quota.included })}
          </p>
          <p className="mt-0.5 text-sm text-slate-700">
            {blocked
              ? t("scan.blockedBody", {
                  used: quota.used,
                  total: quota.included,
                  plan: planName(t, quota.plan, quota.plan_name),
                })
              : quota.paid_active && quota.allow_overage
                ? t("scan.overage", {
                    price: formatMoney((quota.extra_scan_cents || 0) / 100, "EUR"),
                  })
                : t("scan.remaining", { count: quota.remaining })}
          </p>
          <Progress value={pct} className="mt-2 h-1.5 w-56" />
        </div>
      </div>
      {showUpgrade ? (
        <Link to="/app/billing">
          <Button size="sm" data-testid="quota-upgrade-button">
            {t("scan.viewPlans")}
          </Button>
        </Link>
      ) : null}
    </div>
  );
};

export default QuotaBanner;
